<template>
  <tr
    v-if="bluredOrNotHidden"
    :class="{ active: isActive, hovered: showActions }"
    class="draggableBlock"
    data-block="Button"
    @mouseover="mouseOver"
    @mouseleave="mouseOut"
    @focusout="focusOut"
    @click.stop="() => click()"
  >
    <td
      :class="{
        'block-borders': showDragBorder,
        'dragged-opacity': isCurrentElementDragged,
      }"
      :style="wrapperStyle"
      :align="align"
    >
      <blockActions
        v-if="showActions"
        :column-padding-x="columnPaddingX"
        :column-width="columnWidth"
        :is-active="isActive"
        :is-button="true"
        :disable-up-movement="disableUpMovement"
        :disable-down-movement="disableDownMovement"
        :block-height="blockHeight"
        :block-width="blockWidth"
        :is-last-column="isLastColumn"
        :section-index="sectionIndex"
        :column-index="columnIndex"
        :block-index="blockIndex"
        :show-paste="showPaste"
        :copied="copied"
        @duplicate="copyBlock"
        @remove="deleteBlock"
        @moveUp="moveUp"
        @moveDown="moveDown"
        @enrich="enrich"
        @copy="copyToClipboard"
      >
      </blockActions>
      <table
        role="presentation"
        cellpadding="0"
        cellspacing="0"
        style="border-collapse: separate"
        :style="{ width: block.attributes['width'] }"
        :align="align"
        border="0"
      >
        <tbody>
          <tr :style="hiddenStyle">
            <td
              :style="{ ...tdStyle, ...hoverTdSytle }"
              align="center"
              valign="middle"
              :bgcolor="block.attributes['background-color']"
              @mouseover="isHovered = true"
              @mouseleave="isHovered = false"
            >
              <div
                :id="id"
                class="relative max-w-full"
                :style="buttonStyle"
                @click.stop="textClicked"
              >
                <div
                  v-if="aiGenerationLoading"
                  class="absolute z-30 flex h-full w-full items-center justify-center bg-white opacity-60"
                >
                  <div class="light-theme">
                    <TLoadingSpinner :large="true"></TLoadingSpinner>
                  </div>
                </div>
                <inline-text-editor
                  ref="editor"
                  :key="simulatedHover.toString()"
                  v-model="buttonContent"
                  :editor-style="editorStyle"
                  :button-size="true"
                  :context-menu="''"
                  :disable-image="true"
                  :toolbar="[
                    'fontfamily fontsizeinput bold italic underline strikethrough more_button',
                  ]"
                  :select-all="simulatedHover"
                  :inline="true"
                  @onFontSizeChanged="onFontSizeChanged"
                ></inline-text-editor>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </td>
  </tr>
</template>

<script lang="ts" setup>
  import blockProperties from "@/composables/useBlockProperties";
  import blockActions from "./blockActions.vue";
  import InlineTextEditor from "./elements/InlineTextEditor.vue";
  import { TopolButtonBlock } from "@/schemas/template/blocks/buttonBlockSchema";
  import { useMjmlStore } from "@/stores/mjmlStore";
  import { useAiTextStore } from "@/stores/aiTextStore";

  import { TLoadingSpinner } from "@topol/twig-ui";
  import { StyleValue, computed, ref } from "vue";

  const mjmlStore = useMjmlStore();
  const aiTextStore = useAiTextStore();

  const props = defineProps<{
    block: TopolButtonBlock;
    columnIndex: number;
    sectionIndex: number;
    blockIndex: number;
    isLocked: boolean;
  }>();

  const {
    showActions,
    showPaste,
    blockHeight,
    blockWidth,
    simulatedHover,
    id,
    bluredOrNotHidden,
    hiddenStyle,
    isActive,
    disableUpMovement,
    disableDownMovement,
    columnPaddingX,
    columnWidth,
    isCurrentElementDragged,
    showDragBorder,
    isLastColumn,
    copied,
    mouseOver,
    mouseOut,
    click,
    copyBlock,
    deleteBlock,
    moveUp,
    moveDown,
    copyToClipboard,
  } = blockProperties(props);

  const editor = ref(null);
  const isEmpty = ref(false);
  const isHovered = ref(false);

  const aiGenerationLoading = computed(() => {
    return aiTextStore.loading && aiTextStore.blockId === props.block.uid;
  });

  const wrapperStyle = computed<StyleValue>(() => {
    return {
      "word-break": "break-word",
      "font-size": "0px",
      padding: margin.value,
      width: props.block.attributes["width"],
      "background-color": bgColor.value,
    };
  });

  const tdStyle = computed(() => {
    const style = {
      "border-radius": borderRadius.value,
      color: color.value,
      cursor: "auto",
      backgroundColor: props.block.attributes["background-color"],
      border: props.block.attributes["border"],
      width: props.block.attributes["width"],
      "background-image": props.block.attributes["gradient"] || "",
    };
    return style;
  });

  const hoverTdSytle = computed(() => {
    const hover = props.block.hover;
    if (!isHovered.value || !hover) {
      return {};
    }
    const style = {
      backgroundColor: hover["background-color"]
        ? hover["background-color"]
        : props.block.attributes["background-color"],
      color: hover["color"] ? hover["color"] : color.value,
      border: hover["border"]
        ? hover["border"]
        : props.block.attributes["border"],
      "background-image": hover["gradient"]
        ? hover["gradient"]
        : props.block.attributes["gradient"] || "",
    };
    return style;
  });

  const buttonStyle = computed<StyleValue>(() => {
    const style = {
      margin: "0px",
      //@ts-expect-error
      background: props.block.backgroundColor,
      width: props.block.attributes["width"],
      display: "inline-block",
    };
    return style;
  });

  const editorStyle = computed<StyleValue>(() => {
    return {
      padding: innerPadding.value,
      "text-decoration": props.block.attributes["text-decoration"] || "none",
      "line-height": props.block.attributes["line-height"],
      "font-family": fontFamily.value,
      "font-weight": props.block.attributes["font-weight"]
        ? props.block.attributes["font-weight"]
        : "normal",
      "font-style": props.block.attributes["font-style"]
        ? props.block.attributes["font-style"]
        : "normal",
      "text-transform":
        props.block.attributes["text-transform"] || ("none" as any),
      "font-size": fontSize.value,
      color:
        isHovered.value && props.block.hover && props.block.hover["color"]
          ? props.block.hover["color"]
          : color.value,
      "text-align": props.block.attributes["text-align"] || "center",
      "letter-spacing": props.block.attributes["letter-spacing"],
    };
  });

  const fontFamily = computed(() => {
    let fontFamily = props.block.attributes["font-family"]
      ? props.block.attributes["font-family"]
      : "Helvetica";

    //get global font if set
    if (mjmlStore.getBaseAttrString("mj-button", "font-family")) {
      fontFamily = mjmlStore.getBaseAttrString("mj-button", "font-family");
    }

    return fontFamily;
  });

  const margin = computed(() => {
    if (props.block.attributes["padding"]) {
      return props.block.attributes["padding"];
    }
    return "10px 25px";
  });

  const innerPadding = computed(() => {
    const pad = props.block.attributes["inner-padding"];
    if (pad) {
      return pad;
    } else {
      return "10px 25px 10px 25px";
    }
  });

  const borderRadius = computed(() => {
    if (props.block.attributes["border-radius"] == "none") {
      return 0;
    } else {
      return props.block.attributes["border-radius"];
    }
  });

  const align = computed(() => {
    return props.block.attributes["align"]
      ? props.block.attributes["align"]
      : "center";
  });

  const buttonContent = computed({
    get() {
      if (
        isEmpty.value &&
        mjmlStore.mjml.langs &&
        mjmlStore.mjml.langs.length > 1
      ) {
        return "";
      }
      const content = mjmlStore.getSpecificBlockContent(props.block);
      return content
        ? content.replace("<!--[CDATA[", "").replace("]]-->", "")
        : "";
    },
    set(val: string) {
      if (!val || !val.trim()) {
        isEmpty.value = true;
      } else {
        isEmpty.value = false;
      }
      mjmlStore.updateSpecificBlockProp({
        prop: "content",
        value: val,
        sectionIndex: props.sectionIndex,
        columnIndex: props.columnIndex,
        blockIndex: props.blockIndex,
        uid: props.block.uid,
      });
    },
  });

  const color = computed(() => {
    return props.block.attributes["color"]
      ? props.block.attributes["color"]
      : "#ffffff";
  });

  const fontSize = computed(() => {
    return props.block.attributes["font-size"]
      ? props.block.attributes["font-size"]
      : "13px";
  });

  const bgColor = computed(() => {
    const bgColor = props.block.attributes["container-background-color"];
    if (bgColor !== null || bgColor !== undefined) {
      return bgColor;
    }

    return "";
  });

  function textClicked() {
    //@ts-expect-error
    editor.value.focus();
    click();
  }

  function onFontSizeChanged(f: number) {
    updateAttr("font-size", f + "px");
    updateAttr("line-height", f * 1.25 + "px");
  }

  function updateAttr(key: string, value: string) {
    mjmlStore.updateSpecificBlockProp({
      prop: key,
      value: value,
      product: true, //means updating attribute
      sectionIndex: props.sectionIndex,
      columnIndex: props.columnIndex,
      blockIndex: props.blockIndex,
    });
  }

  function focusOut() {
    if (isEmpty.value) {
      const langs = mjmlStore.mjml.langs;
      if (
        langs &&
        langs.length > 1 &&
        !langs.find((l) => l.key === mjmlStore.langToShow)?.primary
      ) {
        isEmpty.value = false;
        return;
      }
      mjmlStore.updateSpecificBlockProp({
        prop: "content",
        value: "Click me!",
        sectionIndex: props.sectionIndex,
        columnIndex: props.columnIndex,
        blockIndex: props.blockIndex,
      });
      isEmpty.value = false;
    }
  }

  function enrich() {
    aiTextStore.sectionIndex = props.sectionIndex;
    aiTextStore.columnIndex = props.columnIndex;
    aiTextStore.blockIndex = props.blockIndex;
    aiTextStore.blockId = props.block.uid;
    aiTextStore.setPromptText(buttonContent.value);
  }

  // onMounted(() => {
  //   if (buttonContent.value === "") {
  //     mjmlStore.updateSpecificBlockProp({
  //       prop: "content",
  //       value: "Click me!",
  //       sectionIndex: props.sectionIndex,
  //       columnIndex: props.columnIndex,
  //       blockIndex: props.blockIndex,
  //     });
  //   }
  // });
</script>
